import { useCallback, useMemo, useState } from "react"
import Box from "@mui/material/Box"
import Container from "@mui/material/Container"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import Button from "@mui/material/Button"
import Tooltip from "@mui/material/Tooltip"
import Snackbar from "@mui/material/Snackbar"
import Alert from "@mui/material/Alert"
import RefreshIcon from "@mui/icons-material/Refresh"
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined"
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import Header from "../../../../components/Header.js"

//CUSTOM IMPORTS
import ComprobantesFilters from "../components/ComprobantesFilters.js"
import ComprobantesTable from "../components/ComprobantesTable.js"
import LoadingTable from "../components/LoadingTable.js"
import ComprobanteDetailDialog from "../components/ComprobanteDetailDialog.js"
import { verEnviarPDF } from "./../../../../services/services.js"
import { formatComprobanteNumber } from "../utils/formatters.js"

const EMPTY_FILTERS = {
  fechaDesde: "",
  fechaHasta: "",
  cliente: "",
  cuit: "",
  tipoComprobante: "",
}

const BRAND = {
  main: "#2471A3",
  dark: "#1B4F72",
  light: "#EAF2F8",
  accent: "#F39C12",
  surface: "#F4F7F9",
  border: "#E1E8ED",
};

function ListadoComprobantesWeb({ comprobantes = [], isLoading = false, isError = false, onRefresh, anularComprobante, sucursalesConsulta }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS)
  const [detailDialog, setDetailDialog] = useState({ open: false, comprobante: null })
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" })

  const [sucursalSelected, setSucursalSelected] = useState(null)
  const [appliedSucursal, setAppliedSucursal] = useState(null)

  const tiposComprobante = useMemo(() => {
    const set = new Set()
    comprobantes?.forEach((c) => {
      if (c?.texto_comprobante) set.add(c.texto_comprobante)
    })
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [comprobantes])

  const filteredData = useMemo(() => {
    const { fechaDesde, fechaHasta, cliente, cuit, tipoComprobante } = appliedFilters
    const desde = fechaDesde ? new Date(fechaDesde) : null
    const hasta = fechaHasta ? new Date(fechaHasta) : null
    if (hasta) hasta.setHours(23, 59, 59, 999)
    const clienteQuery = cliente.trim().toLowerCase()
    const cuitQuery = cuit.replace(/\D/g, "")

    return comprobantes.filter((c) => {
      if (desde || hasta) {
        const fecha = c?.fecha_emision ? new Date(c.fecha_emision) : null
        if (!fecha || Number.isNaN(fecha.getTime())) return false
        if (desde && fecha < desde) return false
        if (hasta && fecha > hasta) return false
      }
      if (clienteQuery && !(c?.razon_social ?? "").toLowerCase().includes(clienteQuery)) {
        return false
      }
      if (cuitQuery && !String(c?.cuit_dni ?? "").replace(/\D/g, "").includes(cuitQuery)) {
        return false
      }
      if (tipoComprobante && c?.texto_comprobante !== tipoComprobante) {
        return false
      }
      if (appliedSucursal?.id && c?.id_sucursal_factura !== appliedSucursal.id) {
        return false
      }

      return true
    })
  }, [comprobantes, appliedFilters, appliedSucursal])

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity })
  }, [])

  const handleSearch = useCallback(() => {
    setAppliedFilters(filters)
    setAppliedSucursal(sucursalSelected)
  }, [filters, sucursalSelected])

  const handleClear = useCallback(() => {
    setFilters(EMPTY_FILTERS)
    setAppliedFilters(EMPTY_FILTERS)
    setSucursalSelected(null)
    setAppliedSucursal(null)
  }, [])

  const handleRefresh = useCallback(() => {
    if (typeof onRefresh === "function") onRefresh()
  }, [onRefresh])

  const handleRowAction = useCallback(
    (action, comprobante) => {
      const numero = formatComprobanteNumber(comprobante.pto_venta, comprobante.nro_comprobante)
      const subtitle = `${comprobante.texto_comprobante || "Comprobante"} N° ${numero}`

      switch (action) {
        case "view-pdf":  
          verEnviarPDF(comprobante.id_factura, 0)
          break

        case "detail":
          setDetailDialog({ open: true, comprobante })
          break

        case "anular-comprobante":
          anularComprobante(comprobante)

        default:
          break
      }
    },
    [showSnackbar],
  )

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Container maxWidth={false} sx={{ py: { xs: 1, md: 2 }, px: { xs: 2, md: 4 } }}>
        <Header title={"Comprobantes Emitidos"} subtitle={"Consulta de comprobantes fiscales emitidos ante ARCA desde la app WEB."}  BRAND={BRAND} />
        <Box sx={{ mb: 1 }}>
          <ComprobantesFilters
            filters={filters}
            onChange={setFilters}
            onSearch={handleSearch}
            onClear={handleClear}
            tiposComprobante={tiposComprobante}
            sucursalesConsulta={sucursalesConsulta}
            setSucursalSelected={setSucursalSelected}
            sucursalSelected={sucursalSelected}
          /> 
        </Box>

        {isError && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            Ocurrió un error al obtener los comprobantes. Por favor, actualizá e intentá nuevamente.
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ bgcolor: "background.paper", borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
            <LoadingTable rows={8} columns={9} /> 
          </Box>
        ) : (
          <ComprobantesTable
            data={filteredData}
            isLoading={isLoading}
            isError={isError}
            onRowAction={handleRowAction}
            onRefresh={handleRefresh}
          />
        )}
      </Container>
      <ComprobanteDetailDialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, comprobante: null })}
        comprobante={detailDialog.comprobante}
      /> 
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>

  )
}

export default ListadoComprobantesWeb
